import React, { useState } from "react";
import { Rocket, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

/**
 * LaunchFireCard
 *
 * One-tap "manually fire the launch announcement everywhere now" button.
 * The auto-fire at 06:55 UK on May 31 should handle it automatically, but
 * this is the safety net in case the schedule task missed it (process
 * restart, deploy mid-window, etc.).
 *
 * Hits POST /api/admin/launch/fire-now which:
 *   1. Force-disables maintenance mode
 *   2. Stops the AI enrichment worker
 *   3. Posts "🚀 LIVE NOW!!" Trip Wall story + pinned Community thread
 *
 * Idempotent — re-tapping does NOT double-post (we check for an existing
 * launch_announcement flag). Use the "Force re-post" button only if the
 * original posts were deleted or you want a fresh banner.
 */
export default function LaunchFireCard() {
  const [state, setState] = useState({ phase: "idle", data: null });

  const fire = async (force = false) => {
    if (force && !window.confirm("Force re-post will create NEW launch posts even if one already exists. Continue?")) return;
    setState({ phase: "running", data: null });
    try {
      const endpoint = force ? "/admin/launch/fire-now-force" : "/admin/launch/fire-now";
      const { data } = await api.post(endpoint);
      setState({ phase: "done", data });
      toast.success("Launch announcement fired — Trip Wall + Community + maintenance off");
    } catch (e) {
      setState({ phase: "failed", data: formatErr(e) });
      toast.error("Fire failed: " + formatErr(e));
    }
  };

  return (
    <div className="mb-10 p-5 border-2 border-[#ff4500] bg-gradient-to-r from-[#fff4ee] to-[#ffefea]" data-testid="launch-fire-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Rocket className="w-3 h-3" strokeWidth={2.2} /> Launch day
          </div>
          <div className="font-display text-xl mt-1">Fire launch announcement now</div>
          <p className="text-sm text-[#555] mt-2 max-w-2xl">
            Auto-fires at <strong>06:55 UK on 31 May</strong>. This is the manual
            safety net if anything goes sideways (deploy missed the window,
            process restart, etc.). Does <strong>three things in one tap</strong>:
          </p>
          <ul className="text-sm text-[#444] mt-2 ml-4 list-disc space-y-1">
            <li>Disables maintenance mode (visitors see the live site immediately)</li>
            <li>Stops the AI enrichment worker (frees up CPU for visitor traffic)</li>
            <li>Posts "🚀 LIVE NOW!! Sign up for a 14-day free Pro trial" pinned to <strong>Trip Wall + Community</strong></li>
          </ul>
          <p className="text-xs text-[#888] mt-3">
            Idempotent — tapping twice does NOT double-post. Use "Force re-post" only
            if you want to nuke and re-create the announcements.
          </p>
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0 w-full sm:w-auto">
          <button
            onClick={() => fire(false)}
            disabled={state.phase === "running"}
            className="btn-primary !px-4 !py-2.5 text-sm inline-flex items-center justify-center gap-2 whitespace-nowrap"
            data-testid="fire-launch-btn"
          >
            {state.phase === "running"
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Firing…</>
              : <><Rocket className="w-4 h-4" strokeWidth={2.2} /> Fire launch now</>}
          </button>
          <button
            onClick={() => fire(true)}
            disabled={state.phase === "running"}
            className="btn-secondary !px-3 !py-1.5 text-[11px] inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
            data-testid="fire-launch-force-btn"
          >
            <RefreshCw className="w-3 h-3" /> Force re-post
          </button>
        </div>
      </div>

      {state.phase === "done" && state.data && (
        <div className="mt-4 p-3 border hairline bg-white text-xs" data-testid="fire-launch-result">
          <div className="flex items-center gap-1.5 text-[#0a8500] font-medium mb-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> Launch fired successfully
          </div>
          <ul className="space-y-0.5 text-[#555] mono">
            <li>· Maintenance disabled: <strong>{state.data.maintenance_disabled ? "yes" : "no"}</strong></li>
            <li>· Worker stopped: <strong>{state.data.worker_stopped ? "yes" : "no"}</strong></li>
            <li>· Wall post: <strong>{state.data.wall_post_id || "(none)"}</strong></li>
            <li>· Community thread: <strong>{state.data.thread_id || "(none)"}</strong></li>
            {state.data.errors?.length > 0 && (
              <li className="text-[#b00020]">· Errors: {state.data.errors.join("; ")}</li>
            )}
          </ul>
        </div>
      )}

      {state.phase === "failed" && (
        <div className="mt-4 p-3 border hairline bg-[#fff4f0] text-xs flex items-start gap-1.5 text-[#b00020]">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {String(state.data)}
        </div>
      )}
    </div>
  );
}
